import React from 'react';
import { Globe, X, Bell, ShoppingBag, CloudSun, AlertTriangle, ShieldCheck, ChevronRight, ToggleLeft as Toggle, Smartphone, Mail, MessageSquare, Volume2, VolumeX, Mic, Square, Trash2, Wind, Play, Flame, CarFront, Info, CheckCircle2, Fingerprint, Lock, Languages, Gift, RefreshCw, Download } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { toast } from 'sonner';
import { CountryCode, COUNTRIES } from '../utils/country-utils';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  preferences: NotificationPreferences;
  onUpdatePreferences: (prefs: NotificationPreferences) => void;
  user: any;
  onUpdateUser: (u: any) => void;
  isSimpleMode?: boolean;
  onToggleSimpleMode?: (enabled: boolean) => void;
  onShowOnboarding?: () => void;
  onOpenReferral?: () => void;
}

export interface NotificationPreferences {
  traffic: boolean;
  weather: boolean;
  market: boolean;
  sos: boolean;
  soundEnabled: boolean;
  biometricsEnabled: boolean;
  loginAlerts: boolean;
  pinEnabled: boolean;
  walletLock: boolean;
  ghostMode: boolean;
  privateProfile: boolean;
  anonymousReporting: boolean;
  pinCode?: string;
  pinAttempts?: number;
  pinLockedUntil?: number;
  trustedDevices: { id: string; name: string; location: string; lastActive: string; isCurrent: boolean }[];
  soundType: 'default' | 'bell' | 'drum' | 'digital' | 'vuvuzela' | 'whistle' | 'custom' | 'lastma' | 'nimet' | 'frsc' | 'fire' | 'police';
  customSoundUri?: string;
  methods: {
    push: boolean;
    email: boolean;
    sms: boolean;
    whatsapp: boolean;
  }
}

const ALERT_SOUNDS: Record<string, string> = {
  default: 'https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3',
  bell: 'https://assets.mixkit.co/active_storage/sfx/1017/1017-preview.mp3',
  drum: 'https://assets.mixkit.co/active_storage/sfx/2571/2571-preview.mp3',
  digital: 'https://assets.mixkit.co/active_storage/sfx/2570/2570-preview.mp3',
  vuvuzela: 'https://assets.mixkit.co/active_storage/sfx/2454/2454-preview.mp3',
  whistle: 'https://assets.mixkit.co/active_storage/sfx/2635/2635-preview.mp3',
  lastma: 'https://assets.mixkit.co/active_storage/sfx/951/951-preview.mp3',
  nimet: 'https://assets.mixkit.co/active_storage/sfx/1110/1110-preview.mp3',
  frsc: 'https://assets.mixkit.co/active_storage/sfx/958/958-preview.mp3', // Sharp Siren
  fire: 'https://assets.mixkit.co/active_storage/sfx/2190/2190-preview.mp3', // Alarm
  police: 'https://assets.mixkit.co/active_storage/sfx/948/948-preview.mp3', // Police Siren
};

export function SettingsModal({ isOpen, onClose, preferences, onUpdatePreferences, user, onUpdateUser, isSimpleMode, onToggleSimpleMode, onShowOnboarding, onOpenReferral }: SettingsModalProps) {
  const [isRecording, setIsRecording] = React.useState(false);
  const [showTrainingTooltip, setShowTrainingTooltip] = React.useState(true);
  const mediaRecorder = React.useRef<MediaRecorder | null>(null);
  const chunks = React.useRef<Blob[]>([]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorder.current = new MediaRecorder(stream);
      chunks.current = [];

      mediaRecorder.current.ondataavailable = (e) => chunks.current.push(e.data);
      mediaRecorder.current.onstop = () => {
        const blob = new Blob(chunks.current, { type: 'audio/ogg; codecs=opus' });
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64data = reader.result as string;
          onUpdatePreferences({
            ...preferences,
            soundType: 'custom',
            customSoundUri: base64data,
            soundEnabled: true
          });
          toast.success('Custom alert recorded!');
        };
        reader.readAsDataURL(blob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.current.start();
      setIsRecording(true);
    } catch (err) {
      toast.error('Could not access microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current && isRecording) {
      mediaRecorder.current.stop();
      setIsRecording(false);
    }
  };

  const togglePreference = (key: keyof Omit<NotificationPreferences, 'methods' | 'soundType' | 'customSoundUri'>) => {
    const newValue = !preferences[key];
    onUpdatePreferences({
      ...preferences,
      [key]: newValue
    });
    
    if (key === 'biometricsEnabled') {
      if (newValue) {
        const currentUser = JSON.parse(localStorage.getItem('sentlo_user') || 'null');
        if (currentUser) {
          localStorage.setItem('sentlo_bio_user', currentUser.email);
          toast.success('Biometrics Enabled', { description: 'You can now sign in with FaceID/Fingerprint.' });
        } else {
          toast.error('Sign in required', { description: 'Please sign in to enable biometrics.' });
        }
      } else {
        localStorage.removeItem('sentlo_bio_user');
        toast.info('Biometrics Disabled');
      }
    } else if (key === 'loginAlerts') {
      toast.success(`Login alerts ${newValue ? 'enabled' : 'disabled'}`, {
        description: newValue ? 'You will receive instant WhatsApp alerts for new logins.' : 'Security alerts muted.'
      });
    } else if (key !== 'soundEnabled') {
      const label = key === 'traffic' ? 'Go-Slow' : key.charAt(0).toUpperCase() + key.slice(1);
      toast.success(`${label} notifications ${newValue ? 'enabled' : 'disabled'}`);
    }
  };

  const setSoundType = (type: NotificationPreferences['soundType']) => {
    onUpdatePreferences({
      ...preferences,
      soundType: type,
      soundEnabled: true
    });
    
    // Preview sound
    const previewUri = type === 'custom' ? preferences.customSoundUri : ALERT_SOUNDS[type];
    if (previewUri) {
      const audio = new Audio(previewUri);
      audio.play().catch(() => {});
    }
  };

  const toggleMethod = (key: keyof NotificationPreferences['methods']) => {
    onUpdatePreferences({
      ...preferences,
      methods: {
        ...preferences.methods,
        [key]: !preferences.methods[key]
      }
    });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-zinc-900/60 backdrop-blur-sm"
          />
          <motion.div
            initial={{ y: '100%', opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="relative w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
          >
            <div className="p-6 overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-green-50 flex items-center justify-center text-green-600">
                    <Bell className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-zinc-900">Notification Settings</h2>
                    <p className="text-xs font-medium text-zinc-500">Choose what you want to stay updated on.</p>
                  </div>
                </div>
                <button onClick={onClose} className="p-2 hover:bg-zinc-100 rounded-full transition-colors">
                  <X className="w-5 h-5 text-zinc-500" />
                </button>
              </div>

              <div className="space-y-6">
                <section>
                  <h3 className="text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-3 ml-1">Region & Localization</h3>
                  <div className="p-4 rounded-2xl border border-zinc-100 bg-white space-y-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-zinc-50 flex items-center justify-center text-zinc-600">
                          <Globe className="w-5 h-5" />
                        </div>
                        <div>
                          <h4 className="text-sm font-bold text-zinc-900">Current Region</h4>
                          <p className="text-[11px] font-medium text-zinc-500">Filters content for your country.</p>
                        </div>
                      </div>
                      <div className="flex gap-1.5">
                        {(Object.values(COUNTRIES) as any[]).filter(c => c.name !== 'Other').map((c) => (
                          <button
                            key={c.name}
                            onClick={() => {
                              onUpdateUser({ ...user, country: c.name });
                              toast.success(`Switched to ${c.name}`, { description: `Your feed is now showing reports for ${c.name}.` });
                            }}
                            className={`w-9 h-9 rounded-xl flex items-center justify-center text-lg transition-all ${user?.country === c.name ? 'bg-green-600 ring-2 ring-green-600/20' : 'bg-zinc-100 grayscale hover:grayscale-0'}`}
                            title={c.name}
                          >
                            {c.flag}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="pt-4 border-t border-zinc-50">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-zinc-50 flex items-center justify-center text-zinc-600">
                            <Languages className="w-5 h-5" />
                          </div>
                          <div>
                            <h4 className="text-sm font-bold text-zinc-900">Manual Language</h4>
                            <p className="text-[11px] font-medium text-zinc-500">Override region localization.</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { id: 'en', label: 'English' },
                          { id: 'yo', label: 'Yorùbá' },
                          { id: 'ig', label: 'Igbo' },
                          { id: 'ha', label: 'Hausa' },
                          { id: 'fr', label: 'Français' },
                          { id: 'gh', label: 'Pidgin' }
                        ].map((lang) => {
                          const isSelected = localStorage.getItem('sentlo_manual_lang') === lang.id;
                          return (
                            <button
                              key={lang.id}
                              onClick={() => {
                                if (isSelected) {
                                  localStorage.removeItem('sentlo_manual_lang');
                                } else {
                                  localStorage.setItem('sentlo_manual_lang', lang.id);
                                }
                                window.location.reload(); // Refresh to apply localization
                              }}
                              className={`py-2 rounded-xl text-[10px] font-black uppercase transition-all ${isSelected ? 'bg-green-600 text-white shadow-lg' : 'bg-zinc-100 text-zinc-500 hover:bg-zinc-200'}`}
                            >
                              {lang.label}
                            </button>
                          );
                        })}
                      </div>
                      <p className="mt-3 text-[9px] font-medium text-zinc-400 italic">
                        By default, Sentlo auto-detects based on your region.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Accessibility & Simple Mode */}
                <section>
                  <h3 className="text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-3 ml-1">Accessibility & Help</h3>
                  <div className="space-y-2">
                    <div className="p-4 rounded-2xl border border-zinc-100 bg-gradient-to-r from-purple-50 to-pink-50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center text-purple-600">
                            <Smartphone className="w-5 h-5" />
                          </div>
                          <div>
                            <h4 className="text-sm font-bold text-zinc-900">🌟 Simple Mode</h4>
                            <p className="text-[11px] font-medium text-zinc-600">Large buttons, easy navigation for everyone</p>
                          </div>
                        </div>
                        <button
                          onClick={() => onToggleSimpleMode?.(!isSimpleMode)}
                          className={`relative w-12 h-7 rounded-full transition-all ${isSimpleMode ? 'bg-purple-600' : 'bg-zinc-300'}`}
                        >
                          <div className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${isSimpleMode ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                      </div>
                    </div>

                    <button
                      onClick={() => {
                        onShowOnboarding?.();
                      }}
                      className="w-full p-4 rounded-2xl border border-zinc-100 bg-white hover:bg-zinc-50 transition-all flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600">
                          <Play className="w-5 h-5" />
                        </div>
                        <div className="text-left">
                          <h4 className="text-sm font-bold text-zinc-900">🎓 Tutorial</h4>
                          <p className="text-[11px] font-medium text-zinc-500">Watch the step-by-step guide again</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-zinc-400" />
                    </button>

                    <div className="p-4 rounded-2xl border border-green-100 bg-green-50">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center text-green-600">
                          <Info className="w-5 h-5" />
                        </div>
                        <div>
                          <h4 className="text-sm font-bold text-zinc-900">💬 Need Help?</h4>
                          <p className="text-[11px] font-medium text-zinc-600">We're here for you 24/7</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <a href="tel:0800-SENTLO-NG" className="px-3 py-2 bg-white rounded-lg text-xs font-bold text-green-600 hover:bg-green-100 transition-all text-center">
                          📞 Call Us
                        </a>
                        <a href="mailto:help@sentlo.ng" className="px-3 py-2 bg-white rounded-lg text-xs font-bold text-green-600 hover:bg-green-100 transition-all text-center">
                          📧 Email
                        </a>
                      </div>
                    </div>

                    {user && onOpenReferral && (
                      <button
                        onClick={() => {
                          onClose();
                          onOpenReferral();
                        }}
                        className="w-full p-4 rounded-2xl border-2 border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 hover:from-amber-100 hover:to-orange-100 transition-all flex items-center justify-between group"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white shadow-lg shadow-amber-500/30">
                            <Gift className="w-5 h-5" />
                          </div>
                          <div className="text-left">
                            <h4 className="text-sm font-bold text-amber-900 flex items-center gap-2">
                              💰 Earn ₦10,000 <span className="text-[8px] bg-red-500 text-white px-1.5 py-0.5 rounded-full">HOT</span>
                            </h4>
                            <p className="text-[11px] font-medium text-amber-700">Share your referral code with friends</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                          <ChevronRight className="w-5 h-5 text-amber-600 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </button>
                    )}
                  </div>
                </section>

                <section>
                  <h3 className="text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-3 ml-1">Alert Categories</h3>
                  <div className="space-y-2">
                    <SettingToggle
                      icon={AlertTriangle}
                      label="Go-Slow (Traffic)"
                      description="Real-time traffic reports in your city."
                      isActive={preferences.traffic}
                      onToggle={() => togglePreference('traffic')}
                      color="text-red-500 bg-red-50"
                    />
                    <SettingToggle
                      icon={CloudSun}
                      label="Weather Updates"
                      description="Rain alerts and NiMet forecasts."
                      isActive={preferences.weather}
                      onToggle={() => togglePreference('weather')}
                      color="text-blue-500 bg-blue-50"
                    />
                    <SettingToggle
                      icon={ShoppingBag}
                      label="Market Prices"
                      description="Price changes for food and fuel."
                      isActive={preferences.market}
                      onToggle={() => togglePreference('market')}
                      color="text-emerald-500 bg-emerald-50"
                    />
                    <SettingToggle
                      icon={ShieldCheck}
                      label="Security & SOS"
                      description="Emergency alerts from neighbors."
                      isActive={preferences.sos}
                      onToggle={() => togglePreference('sos')}
                      color="text-amber-500 bg-amber-50"
                      disabled={true} // SOS always on for safety
                    />
                    <SettingToggle
                      icon={preferences.soundEnabled ? Volume2 : VolumeX}
                      label="Notification Sound"
                      description="Play a sound for new alerts."
                      isActive={preferences.soundEnabled}
                      onToggle={() => togglePreference('soundEnabled')}
                      color="text-purple-500 bg-purple-50"
                    />
                    <SettingToggle
                      icon={Fingerprint}
                      label="Biometric ID"
                      description="Use FaceID or Fingerprint to sign in."
                      isActive={preferences.biometricsEnabled}
                      onToggle={() => togglePreference('biometricsEnabled')}
                      color="text-green-600 bg-green-50"
                    />
                    <SettingToggle
                      icon={ShieldCheck}
                      label="Login Alerts"
                      description="Instant WhatsApp alerts for new logins."
                      isActive={preferences.loginAlerts}
                      onToggle={() => togglePreference('loginAlerts')}
                      color="text-blue-600 bg-blue-50"
                    />
                    <SettingToggle
                      icon={Lock}
                      label="Secure PIN"
                      description="Require a PIN for sensitive actions."
                      isActive={preferences.pinEnabled}
                      onToggle={() => {
                        if (!preferences.pinEnabled) {
                          const pin = prompt('Enter a 4-digit PIN:');
                          if (pin && pin.length === 4 && !isNaN(Number(pin))) {
                            onUpdatePreferences({ ...preferences, pinEnabled: true, pinCode: pin });
                            toast.success('PIN Enabled');
                          } else if (pin) {
                            toast.error('Invalid PIN');
                          }
                        } else {
                          onUpdatePreferences({ ...preferences, pinEnabled: false, pinCode: undefined });
                          toast.info('PIN Disabled');
                        }
                      }}
                      color="text-zinc-600 bg-zinc-50"
                    />

                    {preferences.soundEnabled && (
                      <div className="mt-4 pl-1">
                        <AnimatePresence>
                          {showTrainingTooltip && (
                            <motion.div 
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="mb-4 overflow-hidden"
                            >
                              <div className="bg-green-600 rounded-2xl p-4 text-white relative">
                                <button 
                                  onClick={() => setShowTrainingTooltip(false)}
                                  className="absolute top-2 right-2 p-1 hover:bg-white/20 rounded-lg transition-colors"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                                <div className="flex gap-3">
                                  <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                                    <Info className="w-4 h-4 text-white" />
                                  </div>
                                  <div>
                                    <h5 className="text-[11px] font-black uppercase tracking-wider mb-1">Critical Alert Training</h5>
                                    <p className="text-[10px] font-medium leading-relaxed opacity-90">
                                      Official agencies use authoritative sirens. Use the <Play className="inline w-2 h-2 fill-current" /> buttons below to learn these sounds—recognizing them by ear can save lives during an emergency.
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>

                        <div className="flex justify-between items-end mb-2">
                          <h4 className="text-[10px] font-black uppercase tracking-widest text-zinc-400">Official Agency Packs</h4>
                          <span className="text-[9px] font-bold text-green-600 bg-green-50 px-1.5 py-0.5 rounded uppercase">Verified</span>
                        </div>
                        <div className="grid grid-cols-1 gap-2 mb-4">
                          {[
                            { id: 'lastma', label: 'LASTMA Official', sub: 'Urgent traffic commands', icon: ShieldCheck, color: 'text-yellow-600 bg-yellow-50' },
                            { id: 'nimet', label: 'NiMet Weather', sub: 'Official climate updates', icon: CloudSun, color: 'text-blue-600 bg-blue-50' },
                            { id: 'frsc', label: 'FRSC Road Safety', sub: 'Highway & safety alerts', icon: CarFront, color: 'text-red-600 bg-red-50' },
                            { id: 'fire', label: 'Emergency Services', sub: 'Fire & Rescue response', icon: Flame, color: 'text-orange-600 bg-orange-50' },
                          ].map((pack) => (
                            <div key={pack.id} className="relative group">
                              <button
                                onClick={() => setSoundType(pack.id as any)}
                                className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                                  preferences.soundType === pack.id
                                    ? 'border-green-600 bg-green-50'
                                    : 'border-zinc-100 bg-white hover:border-zinc-200'
                                }`}
                              >
                                <div className={`p-2 rounded-lg ${pack.color}`}>
                                  <pack.icon className="w-4 h-4" />
                                </div>
                                <div className="flex-1">
                                  <div className={`text-xs font-black uppercase ${preferences.soundType === pack.id ? 'text-green-700' : 'text-zinc-700'}`}>
                                    {pack.label}
                                  </div>
                                  <div className="text-[10px] font-medium text-zinc-400">
                                    {pack.sub}
                                  </div>
                                </div>
                                {preferences.soundType === pack.id && (
                                  <div className="mr-8">
                                    <div className="w-1.5 h-1.5 rounded-full bg-green-600 animate-pulse" />
                                  </div>
                                )}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const audio = new Audio(ALERT_SOUNDS[pack.id]);
                                  audio.play().catch(console.error);
                                }}
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg bg-zinc-100 hover:bg-zinc-200 text-zinc-600 transition-colors"
                                title="Test Authority Sound"
                              >
                                <Play className="w-3.5 h-3.5 fill-current" />
                              </button>
                            </div>
                          ))}
                        </div>

                        <div className="flex justify-between items-end mb-2">
                          <h4 className="text-[10px] font-black uppercase tracking-widest text-zinc-400">Sentlo Core Library</h4>
                          <button 
                            onClick={isRecording ? stopRecording : startRecording}
                            className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase transition-all ${
                              isRecording 
                                ? 'bg-red-100 text-red-600 animate-pulse' 
                                : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                            }`}
                          >
                            {isRecording ? <Square className="w-2.5 h-2.5" /> : <Mic className="w-2.5 h-2.5" />}
                            {isRecording ? 'Stop Recording' : 'Record Custom'}
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {[
                            { id: 'default', label: 'Sentlo Ding', icon: Bell },
                            { id: 'bell', label: 'Naija Bell', icon: Bell },
                            { id: 'drum', label: 'Talking Drum', icon: Volume2 },
                            { id: 'vuvuzela', label: 'Vuvuzela', icon: Wind },
                            { id: 'whistle', label: 'Traffic Whistle', icon: Volume2 },
                            { id: 'digital', label: 'Digital Chirp', icon: Smartphone },
                          ].map((sound) => (
                            <button
                              key={sound.id}
                              onClick={() => setSoundType(sound.id as any)}
                              className={`flex items-center gap-2 p-3 rounded-xl border text-xs font-bold transition-all ${
                                preferences.soundType === sound.id
                                  ? 'border-green-600 bg-green-50 text-green-700'
                                  : 'border-zinc-100 bg-white text-zinc-500 hover:border-zinc-200'
                              }`}
                            >
                              <sound.icon className="w-3.5 h-3.5" />
                              {sound.label}
                            </button>
                          ))}
                          {preferences.customSoundUri && (
                            <button
                              onClick={() => setSoundType('custom')}
                              className={`flex items-center justify-between gap-2 p-3 rounded-xl border text-xs font-bold transition-all col-span-2 ${
                                preferences.soundType === 'custom'
                                  ? 'border-green-600 bg-green-50 text-green-700'
                                  : 'border-zinc-100 bg-white text-zinc-500 hover:border-zinc-200'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <Mic className="w-3.5 h-3.5" />
                                Your Custom Recording
                              </div>
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onUpdatePreferences({ ...preferences, customSoundUri: undefined, soundType: 'default' });
                                }}
                                className="p-1 hover:bg-red-50 text-zinc-400 hover:text-red-500 rounded-lg transition-colors"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </section>

                <section>
                  <h3 className="text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-3 ml-1">Delivery Channels</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <MethodButton
                      icon={Smartphone}
                      label="Push"
                      isActive={preferences.methods.push}
                      onClick={() => toggleMethod('push')}
                    />
                    <MethodButton
                      icon={Mail}
                      label="Email"
                      isActive={preferences.methods.email}
                      onClick={() => toggleMethod('email')}
                    />
                    <MethodButton
                      icon={MessageSquare}
                      label="SMS"
                      isActive={preferences.methods.sms}
                      onClick={() => toggleMethod('sms')}
                    />
                    <MethodButton
                      icon={MessageSquare}
                      label="WhatsApp"
                      isActive={preferences.methods.whatsapp}
                      onClick={() => toggleMethod('whatsapp')}
                    />
                  </div>
                </section>

                {/* PWA & Storage Section */}
                <section>
                  <h3 className="text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-3 ml-1">App & Storage</h3>
                  <div className="space-y-2">
                    <button
                      onClick={async () => {
                        if (confirm('Clear app cache? This will free up storage and refresh all content. The app will reload.')) {
                          if ('caches' in window) {
                            const cacheNames = await caches.keys();
                            await Promise.all(cacheNames.map(name => caches.delete(name)));
                          }
                          toast.success('Cache cleared! Reloading app...');
                          setTimeout(() => window.location.reload(), 1000);
                        }
                      }}
                      className="w-full flex items-center justify-between p-4 rounded-2xl border border-zinc-100 bg-white hover:border-zinc-200 transition-all"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-blue-50 text-blue-600">
                          <RefreshCw className="w-5 h-5" />
                        </div>
                        <div className="text-left">
                          <h4 className="text-sm font-bold text-zinc-900">Clear Cache</h4>
                          <p className="text-[11px] font-medium text-zinc-500">Free up storage & refresh content</p>
                        </div>
                      </div>
                      <div className="text-xs text-zinc-400">
                        →
                      </div>
                    </button>
                    
                    {window.matchMedia('(display-mode: standalone)').matches ? (
                      <div className="p-4 rounded-2xl border border-emerald-100 bg-emerald-50">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-emerald-600 text-white">
                            <Smartphone className="w-5 h-5" />
                          </div>
                          <div>
                            <h4 className="text-sm font-bold text-emerald-900">App Installed ✅</h4>
                            <p className="text-[11px] font-medium text-emerald-700">Running as standalone app</p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          toast.info('Look for the install prompt at the bottom of your screen, or tap the share button (iOS) or browser menu to install!');
                        }}
                        className="w-full flex items-center justify-between p-4 rounded-2xl border border-zinc-100 bg-white hover:border-emerald-200 hover:bg-emerald-50 transition-all"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-emerald-50 text-emerald-600">
                            <Download className="w-5 h-5" />
                          </div>
                          <div className="text-left">
                            <h4 className="text-sm font-bold text-zinc-900">Install App</h4>
                            <p className="text-[11px] font-medium text-zinc-500">Add to home screen for faster access</p>
                          </div>
                        </div>
                        <div className="text-xs text-emerald-600 font-bold">
                          TRY IT
                        </div>
                      </button>
                    )}
                  </div>
                </section>

                <div className="pt-2">
                  <button
                    onClick={onClose}
                    className="w-full bg-zinc-900 text-white py-4 rounded-2xl font-bold hover:bg-zinc-800 transition-all shadow-xl active:scale-[0.98]"
                  >
                    Save Preferences
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

function SettingToggle({ icon: Icon, label, description, isActive, onToggle, color, disabled = false }: any) {
  return (
    <div 
      className={`p-4 rounded-2xl border transition-all flex items-center justify-between ${
        isActive ? 'border-zinc-200 bg-zinc-50/50' : 'border-zinc-100 bg-white opacity-60'
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <h4 className="text-sm font-bold text-zinc-900">{label}</h4>
          <p className="text-[11px] font-medium text-zinc-500">{description}</p>
        </div>
      </div>
      <button 
        disabled={disabled}
        onClick={onToggle}
        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden ${
          isActive ? 'bg-green-600' : 'bg-zinc-200'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${
            isActive ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );
}

function MethodButton({ icon: Icon, label, isActive, onClick }: any) {
  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center gap-2 p-3 rounded-2xl border transition-all ${
        isActive 
          ? 'border-green-600 bg-green-50 text-green-700 font-bold' 
          : 'border-zinc-100 bg-white text-zinc-400'
      }`}
    >
      <Icon className="w-5 h-5" />
      <span className="text-[10px] uppercase tracking-wider">{label}</span>
    </button>
  );
}